/**
 * Adaptive arcade score.
 *
 * The track is generated once from a seed and then performed live: eight stems
 * over a 16-step grid, each stem mixed by game state. Nothing is pre-rendered,
 * nothing streams from disk, and two runs with the same seed produce the same
 * melody — which matters, because the visual regression shots are captured
 * against a running game.
 *
 * Scheduling follows the standard Web Audio lookahead pattern: a coarse timer
 * wakes up every 25 ms and books every step that falls inside the next 150 ms
 * of context time. Sample-accurate, immune to timer jitter, and it survives a
 * `suspend()`/`resume()` cycle without smearing.
 *
 * State changes are queued and applied on the next bar line. Cutting a phrase
 * in half is the single loudest way to make a soundtrack sound procedural.
 */

import { seedFrom } from '@/core/rng';
import type { SynthEngine, TriggerOpts, VoiceSpec, Voice } from './Synth';

export type MusicState = 'silent' | 'menu' | 'prep' | 'combat' | 'boss' | 'defeat' | 'victory';

type Stem = 'pad' | 'bass' | 'kick' | 'hat' | 'snare' | 'arp' | 'lead' | 'tension';

const STEMS: readonly Stem[] = ['pad', 'bass', 'kick', 'hat', 'snare', 'arp', 'lead', 'tension'];

/** Mix targets per state. `tension` is driven by enemy pressure, not by state. */
const MIX: Record<MusicState, Record<Stem, number>> = {
  silent: { pad: 0, bass: 0, kick: 0, hat: 0, snare: 0, arp: 0, lead: 0, tension: 0 },
  menu: { pad: 0.85, bass: 0.35, kick: 0, hat: 0.18, snare: 0, arp: 0.3, lead: 0, tension: 0 },
  prep: { pad: 0.7, bass: 0.5, kick: 0.35, hat: 0.4, snare: 0, arp: 0.42, lead: 0, tension: 0 },
  combat: { pad: 0.45, bass: 1.0, kick: 1.0, hat: 0.8, snare: 0.9, arp: 0.75, lead: 0.6, tension: 0 },
  boss: { pad: 0.6, bass: 1.0, kick: 1.0, hat: 0.9, snare: 1.0, arp: 0.5, lead: 0.95, tension: 0.5 },
  defeat: { pad: 0, bass: 0, kick: 0, hat: 0, snare: 0, arp: 0, lead: 0, tension: 0 },
  victory: { pad: 0, bass: 0, kick: 0, hat: 0, snare: 0, arp: 0, lead: 0, tension: 0 },
};

/** Tempo window. Combat leans on the top half, prep on the bottom. */
const BPM_MIN = 92;
const BPM_MAX = 152;

/** Natural minor. Boss mode swaps in Phrygian for the flat second. */
const MINOR = [0, 2, 3, 5, 7, 8, 10] as const;
const PHRYGIAN = [0, 1, 3, 5, 7, 8, 10] as const;

/** Degree per bar. i – VI – iv – V reads as arcade-heroic without trying. */
const PROG_MAIN = [0, 5, 3, 4] as const;
/** Boss: i – bII – i – V. The flat second is the whole point. */
const PROG_BOSS = [0, 1, 0, 4] as const;

/** A minor, one octave below middle. Boss drops a minor third to F#. */
const ROOT_MIDI = 57;
const BOSS_ROOT_MIDI = 54;

const PHRASES = 4;
const STEPS = 16;
const LOOKAHEAD_MS = 25;
const SCHEDULE_AHEAD = 0.15;

const midiHz = (m: number) => 440 * Math.pow(2, (m - 69) / 12);
const clamp01 = (v: number) => (v < 0 ? 0 : v > 1 ? 1 : v);

export class Music {
  private readonly engine: SynthEngine;
  private readonly rng: () => number;

  /** Per-phrase step masks. Regenerated never — repetition is what makes it a song. */
  private readonly bassPat: Uint8Array;
  private readonly hatPat: Uint8Array;
  private readonly arpPat: Uint8Array;
  private readonly leadPat: Int8Array;
  private readonly kickPat: Uint8Array;
  private readonly snarePat: Uint8Array;

  private readonly gain: Float32Array;
  private readonly target: Float32Array;

  private state: MusicState = 'silent';
  private pending: MusicState | null = null;
  private intensity = 0;
  private pressure = 0;

  private bpm = BPM_MIN;
  private targetBpm = BPM_MIN;
  private step = 0;
  private bar = 0;
  private nextTime = 0;
  private playing = false;
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Reused per-note patch. Mutated then handed straight to `Voice.trigger`. */
  private readonly spec: VoiceSpec = { freq: 220, dur: 0 };
  private readonly opts: TriggerOpts = { pan: 0, gain: 1, detune: 0, timeScale: 1, loop: false, priority: 0 };

  constructor(engine: SynthEngine, seed = 'pixeltd-score') {
    this.engine = engine;
    this.rng = seedFrom(seed);

    this.bassPat = new Uint8Array(PHRASES * STEPS);
    this.hatPat = new Uint8Array(PHRASES * STEPS);
    this.arpPat = new Uint8Array(PHRASES * STEPS);
    this.leadPat = new Int8Array(PHRASES * STEPS);
    this.kickPat = new Uint8Array(PHRASES * STEPS);
    this.snarePat = new Uint8Array(PHRASES * STEPS);
    this.gain = new Float32Array(STEMS.length);
    this.target = new Float32Array(STEMS.length);

    this.compose();
  }

  // ───────────────────────────────────────────────────────────── authoring ──

  /** Writes the whole arrangement once, from the seed. */
  private compose(): void {
    const r = this.rng;
    for (let p = 0; p < PHRASES; p++) {
      const base = p * STEPS;
      // The last phrase is the turnaround: busier fills, a melodic answer.
      const fill = p === PHRASES - 1;

      for (let s = 0; s < STEPS; s++) {
        const i = base + s;

        // Kick: downbeat always, plus a syncopated pickup or two.
        this.kickPat[i] = s === 0 || s === 8 ? 1 : (s === 11 || s === 14) && r() < 0.45 ? 1 : 0;
        // Snare on the backbeat, with ghost notes in the turnaround.
        this.snarePat[i] = s === 4 || s === 12 ? 1 : fill && s > 12 && r() < 0.5 ? 1 : 0;
        // Hats on the offbeat eighths, thinning out at random.
        this.hatPat[i] = s % 2 === 1 ? (r() < 0.82 ? 1 : 0) : r() < 0.3 ? 1 : 0;
        // Bass: root on the beat, then a seeded eighth-note walk.
        this.bassPat[i] = s % 4 === 0 ? 1 : s % 2 === 0 ? (r() < 0.55 ? 1 : 0) : r() < 0.2 ? 1 : 0;
        // Arp runs sixteenths but drops a few for phrasing.
        this.arpPat[i] = r() < 0.78 ? 1 : 0;
        // Lead: sparse, on strong steps, -1 means rest.
        this.leadPat[i] = (s % 4 === 0 || (fill && s % 2 === 0)) && r() < 0.62
          ? Math.floor(r() * 5)
          : -1;
      }
    }
  }

  // ─────────────────────────────────────────────────────────────── control ──

  /** Queued to the next bar line. `silent` and the endings take effect at once. */
  setState(next: MusicState): void {
    if (next === this.state && this.pending === null) return;
    if (next === 'silent' || next === 'defeat' || next === 'victory') {
      this.pending = null;
      this.state = next;
      this.applyMix();
      // Endings are stings, not transitions: fade the loop out under them.
      for (let i = 0; i < this.gain.length; i++) this.gain[i] = 0;
      return;
    }
    // Coming out of silence there is no phrase to protect, and waiting for a
    // bar line would just be up to two seconds of nothing at boot.
    if (this.state === 'silent') {
      this.state = next;
      this.pending = null;
      this.step = 0;
      this.bar = 0;
      this.nextTime = Math.max(this.nextTime, this.engine.now + 0.05);
      this.applyMix();
      return;
    }
    this.pending = next;
  }

  /** 0..1 wave intensity. Eases the tempo; applied on the next bar line. */
  setIntensity(v: number): void {
    this.intensity = clamp01(v);
    this.targetBpm = BPM_MIN + (BPM_MAX - BPM_MIN) * this.intensity;
  }

  /** 0..1 enemy pressure. Above 0.7 the tension stem fades in. */
  setPressure(v: number): void {
    this.pressure = clamp01(v);
    this.applyMix();
  }

  get currentState(): MusicState { return this.state; }
  get currentBpm(): number { return this.bpm; }

  start(): void {
    if (this.playing) return;
    this.playing = true;
    this.nextTime = this.engine.now + 0.08;
    this.step = 0;
    this.bar = 0;
    this.applyMix();
    if (this.timer === null) this.timer = setInterval(this.pump, LOOKAHEAD_MS);
  }

  stop(): void {
    this.playing = false;
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
  }

  dispose(): void { this.stop(); }

  // ───────────────────────────────────────────────────────────── scheduler ──

  private readonly pump = (): void => {
    if (!this.playing) return;
    const ctx = this.engine.ctx;
    if (ctx.state !== 'running') return;

    const now = ctx.currentTime;
    // A tab that was hidden for a minute must not try to play a minute of
    // music in one go.
    if (this.nextTime < now - 0.25) this.nextTime = now + 0.05;

    const horizon = now + SCHEDULE_AHEAD;
    let guard = 0;
    while (this.nextTime < horizon && guard++ < 64) {
      this.scheduleStep(this.step, this.nextTime);
      this.advance();
    }
  };

  private advance(): void {
    this.nextTime += 15 / this.bpm; // one sixteenth = 60/bpm/4
    this.step++;
    if (this.step >= STEPS) {
      this.step = 0;
      this.bar++;
      this.onBar();
    }
    // Walk each stem toward its target across a bar, so layers breathe in.
    for (let i = 0; i < this.gain.length; i++) {
      const g = this.gain[i];
      const t = this.target[i];
      this.gain[i] = g + (t - g) * 0.09;
    }
  }

  /** Bar line: the only place the arrangement is allowed to change. */
  private onBar(): void {
    if (this.pending !== null) {
      this.state = this.pending;
      this.pending = null;
      this.applyMix();
    }
    // Tempo eases rather than jumps; a hard BPM change reads as a glitch.
    this.bpm += (this.targetBpm - this.bpm) * 0.34;
    this.engine.musicFx.setDelayTime(45 / this.bpm, this.engine.now); // dotted eighth
  }

  private applyMix(): void {
    const mix = MIX[this.state];
    for (let i = 0; i < STEMS.length; i++) this.target[i] = mix[STEMS[i]];
    // Tension rides on top of whatever the state asked for.
    if (this.state === 'combat' || this.state === 'boss' || this.state === 'prep') {
      const t = this.pressure <= 0.7 ? 0 : (this.pressure - 0.7) / 0.3;
      this.target[7] = Math.max(this.target[7], t * 0.75);
      // Pressure also pushes the lead forward.
      this.target[6] = Math.min(1, this.target[6] + t * 0.3);
    }
  }

  private stemGain(s: Stem): number { return this.gain[STEMS.indexOf(s)]; }

  // ────────────────────────────────────────────────────────────── sequence ──

  private scheduleStep(step: number, at: number): void {
    if (this.state === 'silent' || this.state === 'defeat' || this.state === 'victory') return;

    const boss = this.state === 'boss';
    const scale = boss ? PHRYGIAN : MINOR;
    const prog = boss ? PROG_BOSS : PROG_MAIN;
    const root = (boss ? BOSS_ROOT_MIDI : ROOT_MIDI);
    const degree = prog[this.bar % prog.length];
    const chordRoot = root + scale[degree % scale.length];
    const phrase = (this.bar % PHRASES) * STEPS + step;
    const sixteenth = 15 / this.bpm;

    if (step === 0) this.pad(chordRoot, at, scale, degree, sixteenth);
    this.drums(phrase, at);
    this.bass(phrase, chordRoot, at, sixteenth);
    this.arp(phrase, chordRoot, at, scale, sixteenth);
    this.lead(phrase, root, at, scale, sixteenth, boss);
    this.tension(step, root, at, sixteenth, boss);
  }

  private pad(chordRoot: number, at: number, scale: readonly number[], degree: number, sixteenth: number): void {
    const g = this.stemGain('pad');
    if (g < 0.02) return;
    const third = scale[(degree + 2) % scale.length] - scale[degree % scale.length];
    for (let n = 0; n < 2; n++) {
      const v = this.engine.music.alloc(10, this.engine.now);
      if (v === null) continue;
      this.resetSpec();
      const s = this.spec;
      s.wave = 'saw'; s.wave2 = 'saw'; s.mix2 = 0.5;
      s.detune2 = n === 0 ? 8 : -11;
      s.freq = midiHz(chordRoot + (n === 0 ? 0 : third < 0 ? third + 12 : third));
      s.filter = 'lowpass'; s.cutoff = 900 + this.intensity * 900; s.q = 1.4;
      s.attack = 0.35; s.decay = 0.4; s.sustain = 0.8; s.release = 0.9;
      s.dur = Math.max(0.1, sixteenth * STEPS - 0.6);
      s.peak = 0.09 * g;
      s.reverb = 0.55;
      s.lfoHz = 0.18; s.lfoCents = 6;
      this.fire(v, at, 10);
    }
  }

  private drums(phrase: number, at: number): void {
    const gk = this.stemGain('kick');
    if (gk > 0.02 && this.kickPat[phrase] === 1) {
      const v = this.engine.music.alloc(14, this.engine.now);
      if (v !== null) {
        this.resetSpec();
        const s = this.spec;
        s.wave = 'sine'; s.noise = 0.05; s.noiseRate = 0.6;
        s.freq = 155; s.freqEnd = 44; s.sweepTime = 0.09;
        s.cutoff = 1400; s.q = 0.9;
        s.attack = 0.002; s.decay = 0.2; s.release = 0.06;
        s.peak = 0.62 * gk;
        this.fire(v, at, 14);
      }
    }

    const gs = this.stemGain('snare');
    if (gs > 0.02 && this.snarePat[phrase] === 1) {
      const v = this.engine.music.alloc(13, this.engine.now);
      if (v !== null) {
        this.resetSpec();
        const s = this.spec;
        s.wave = 'tri'; s.noise = 0.8; s.noiseRate = 1.4;
        s.freq = 220; s.freqEnd = 170; s.sweepTime = 0.06;
        s.filter = 'bandpass'; s.cutoff = 1900; s.q = 1.1;
        s.attack = 0.001; s.decay = 0.14; s.release = 0.05;
        s.peak = 0.34 * gs; s.reverb = 0.22;
        this.fire(v, at, 13);
      }
    }

    const gh = this.stemGain('hat');
    if (gh > 0.02 && this.hatPat[phrase] === 1) {
      const v = this.engine.music.alloc(8, this.engine.now);
      if (v !== null) {
        const open = phrase % 8 === 7;
        this.resetSpec();
        const s = this.spec;
        s.noise = 1; s.noiseRate = 2.6;
        s.freq = 6000;
        s.filter = 'highpass'; s.cutoff = 6800; s.q = 1.2;
        s.attack = 0.001; s.decay = open ? 0.12 : 0.032; s.release = 0.02;
        s.peak = (open ? 0.16 : 0.11) * gh;
        this.fire(v, at, 8);
      }
    }
  }

  private bass(phrase: number, chordRoot: number, at: number, sixteenth: number): void {
    const g = this.stemGain('bass');
    if (g < 0.02 || this.bassPat[phrase] === 0) return;
    const v = this.engine.music.alloc(12, this.engine.now);
    if (v === null) return;
    const octave = (phrase % 8) === 6 ? 12 : 0;
    this.resetSpec();
    const s = this.spec;
    s.wave = 'pulse'; s.width = 0.32; s.mix2 = 0.35; s.wave2 = 'saw'; s.ratio2 = 0.5;
    s.freq = midiHz(chordRoot - 12 + octave);
    s.cutoff = 380 + this.intensity * 620; s.q = 3.4;
    s.attack = 0.004; s.decay = 0.07; s.sustain = 0.55; s.release = 0.05;
    s.dur = sixteenth * 0.7;
    s.peak = 0.3 * g;
    this.fire(v, at, 12);
  }

  private arp(phrase: number, chordRoot: number, at: number, scale: readonly number[], sixteenth: number): void {
    const g = this.stemGain('arp');
    if (g < 0.02 || this.arpPat[phrase] === 0) return;
    const v = this.engine.music.alloc(6, this.engine.now);
    if (v === null) return;
    const idx = phrase % 6;
    const chordTone = [0, 2, 4, 2, 4, 6][idx];
    const oct = idx >= 3 ? 12 : 0;
    this.resetSpec();
    const s = this.spec;
    s.freq = midiHz(chordRoot + scale[chordTone % scale.length] + Math.floor(chordTone / scale.length) * 12 + oct);
    s.cutoff = 2200 + this.intensity * 2400; s.q = 1.6;
    s.attack = 0.002; s.decay = 0.06; s.sustain = 0.3; s.release = 0.07;
    s.dur = sixteenth * 0.5;
    s.peak = 0.13 * g; s.reverb = 0.12; s.echo = 0.22;
    this.fire(v, at, 6);
  }

  private lead(phrase: number, root: number, at: number, scale: readonly number[], sixteenth: number, boss: boolean): void {
    const g = this.stemGain('lead');
    const note = this.leadPat[phrase];
    if (g < 0.02 || note < 0) return;
    const v = this.engine.music.alloc(11, this.engine.now);
    if (v === null) return;
    this.resetSpec();
    const s = this.spec;
    s.wave = boss ? 'saw' : 'tri';
    s.mix2 = boss ? 0.4 : 0; s.wave2 = 'square'; s.detune2 = 9;
    s.freq = midiHz(root + 12 + scale[note % scale.length] + (note >= scale.length ? 12 : 0));
    s.cutoff = 2600 + this.intensity * 2600; s.q = 2.2;
    s.attack = 0.01; s.decay = 0.12; s.sustain = 0.5; s.release = 0.18;
    s.dur = sixteenth * 2.2;
    s.peak = 0.16 * g; s.reverb = 0.28; s.echo = 0.3;
    s.lfoHz = 5.4; s.lfoCents = 14;
    this.fire(v, at, 11);
  }

  /** Above 70% pressure: a high tritone drone that will not let you relax. */
  private tension(step: number, root: number, at: number, sixteenth: number, boss: boolean): void {
    const g = this.stemGain('tension');
    if (g < 0.02 || step % 8 !== 0) return;
    const v = this.engine.music.alloc(9, this.engine.now);
    if (v === null) return;
    this.resetSpec();
    const s = this.spec;
    s.wave = 'saw'; s.mix2 = 0.5; s.wave2 = 'saw'; s.ratio2 = 1.4142; s.detune2 = 14;
    s.freq = midiHz(root + 24 + (boss ? 1 : 0));
    s.filter = 'bandpass'; s.cutoff = 1800; s.q = 4.5;
    s.attack = 0.25; s.decay = 0.3; s.sustain = 0.6; s.release = 0.5;
    s.dur = sixteenth * 7;
    s.peak = 0.09 * g; s.reverb = 0.45; s.echo = 0;
    s.lfoHz = 0.9; s.lfoCents = 26; s.lfoCutoff = 400;
    this.fire(v, at, 9);
  }

  /**
   * Clears every optional field. The patch object is shared across all eight
   * stems, so a leftover `noiseRate` from the hi-hat would otherwise turn up
   * inside the pad two steps later.
   */
  private resetSpec(): void {
    const s = this.spec;
    s.wave = 'square'; s.width = 0.5;
    s.freq = 220; s.freqEnd = undefined; s.sweepShape = 'exp'; s.sweepTime = undefined;
    s.wave2 = undefined; s.mix2 = 0; s.ratio2 = 1; s.detune2 = 0;
    s.noise = 0; s.noiseRate = 1;
    s.filter = 'lowpass'; s.cutoff = 18000; s.cutoffEnd = undefined; s.q = 0.7;
    s.attack = 0.005; s.decay = 0.08; s.sustain = 0; s.release = 0.06; s.peak = 0.2; s.dur = 0;
    s.reverb = 0; s.echo = 0;
    s.lfoHz = 5; s.lfoCents = 0; s.lfoCutoff = 0;
  }

  private fire(v: Voice, at: number, priority: number): void {
    this.opts.pan = 0;
    this.opts.gain = 1;
    this.opts.detune = 0;
    this.opts.timeScale = 1;
    this.opts.loop = false;
    this.opts.priority = priority;
    v.trigger(this.spec, at, this.opts);
  }
}
